import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  alpha,
  useTheme,
  AppBar,
  Toolbar,
  Icon,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Stack,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Divider,
  Skeleton,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { usePOSService } from "../../hooks/usePOSService";
import { formatCurrency } from "../../utils/formatters";
import dayjs, { Dayjs } from "dayjs";
import {
  DaybookSession as ServiceDaybookSession,
  DaybookOrder,
  DaybookTransaction,
} from "../../../../services/pos.service";
import { toast } from "react-toastify";
import { TabContext, TabPanel } from "@mui/lab";
import { DayBookOrdersPanel } from "./daybook.orders.panel";
import { DayBookTransactionsPanel } from "./daybook.transactions.panel";
import { DayBookSummaryPanel } from "./daybook.summary.panel";
import { DateRangeSelector } from "../../components/DateRangeSelector/DateRangeSelector";
import { DaybookSession as ComponentDaybookSession } from "./types";

export const DayBookPage: React.FC = () => {
  const theme = useTheme();
  const isProcessing = localStorage.getItem("paymentInProcess");
  const draftId = localStorage.getItem("DraftId");
  if (isProcessing) {
    localStorage.removeItem("paymentInProcess");
    console.log("Cleared paymentInProcess after modal close");
  }
  if (draftId) {
    localStorage.removeItem("DraftId");
    console.log("Cleared DraftId after modal close");
  }
  const { posService: service } = usePOSService();
  const [ordersLoading, setOrdersLoading] = React.useState(true);
  const [transactionsLoading, setTransactionsLoading] = React.useState(true);

  const [sessionsLoading, setSessionsLoading] = React.useState(true);
  const [selectedDate, setSelectedDate] = React.useState<Dayjs>(dayjs());
  const [sessions, setSessions] = React.useState<ServiceDaybookSession[]>([]);
  const [selectedSession, setSelectedSession] = React.useState<string>("");
  const [activeTab, setActiveTab] = React.useState(0);
  const [orders, setOrders] = React.useState<DaybookOrder[]>([]);
  const [transactions, setTransactions] = React.useState<DaybookTransaction[]>(
    []
  );
  const [expandedBranches, setExpandedBranches] = React.useState<string[]>([]);

  const fetchSessions = () => {
    setSessionsLoading(true);
    service
      .getDaybookSessions({
        date: selectedDate.valueOf(),
        pageIndex: 1,
        pageSize: 50,
      })
      .then((response) => {
        if (response.success) {
          setSessions(response.data.items);
          if (response.data.items.length > 0) {
            setSelectedSession(response.data.items[0].id);
          }
        }
      })
      .catch((error) => {
        console.error("Error fetching sessions:", error);
      })
      .finally(() => {
        setSessionsLoading(false);
      });
  };

  const fetchSessionOrders = () => {
    setOrdersLoading(true);

    service
      .getDaybookSessionOrders({
        session_id: selectedSession,
        pageIndex: 1,
        pageSize: 50,
      })
      .then((ordersResponse) => {
        if (ordersResponse.success) {
          setOrders(ordersResponse.data.items);
        }
      })
      .catch((error) => {
        toast.error("Error fetching orders list");
      })
      .finally(() => {
        setOrdersLoading(false);
      });
  };

  const fetchSessionTransactions = () => {
    setTransactionsLoading(true);

    service
      .getDaybookSessionTransactions({
        session_id: selectedSession,
        pageIndex: 1,
        pageSize: 50,
      })
      .then((transactionsResponse) => {
        if (transactionsResponse.success) {
          setTransactions(transactionsResponse.data.items);
        }
      })
      .catch((error) => {
        toast.error("Error fetching transactions list");
      })
      .finally(() => {
        setTransactionsLoading(false);
      });
  };

  useEffect(fetchSessions, [selectedDate]);
  useEffect(() => {
    if (selectedSession) {
      fetchSessionOrders();
      fetchSessionTransactions();
    }
  }, [selectedSession]);

  const groupedSessions = React.useMemo(() => {
    const groups: { [key: string]: ServiceDaybookSession[] } = {};
    sessions.forEach((session) => {
      const branchName = session.business?.branch_name || "Unknown Branch";
      if (!groups[branchName]) {
        groups[branchName] = [];
      }
      groups[branchName].push(session);
    });
    return groups;
  }, [sessions]);

  const toggleBranch = (branchName: string) => {
    setExpandedBranches((prev) =>
      prev.includes(branchName)
        ? prev.filter((b) => b !== branchName)
        : [...prev, branchName]
    );
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      {/* Custom AppBar */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "#1E8078",
          borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
            Daybook
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ flex: 1, p: 2, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "100%",
            maxWidth: 1920,
            mx: "auto",
          }}
        >
          {/* Filters */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <DateRangeSelector
                  mode="single"
                  value={selectedDate}
                  onChange={(date: Dayjs | null) =>
                    date && setSelectedDate(date)
                  }
                  maxDate={dayjs()}
                  variant="primary"
                />
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedDate(dayjs())}
                    startIcon={<Icon>today</Icon>}
                    sx={{
                      borderColor: alpha("#1E8078", 0.3),
                      color: "#1E8078",
                      "&:hover": {
                        borderColor: "#1E8078",
                        backgroundColor: alpha("#1E8078", 0.05),
                      },
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedDate(dayjs().subtract(1, "day"))}
                    startIcon={<Icon>history</Icon>}
                    sx={{
                      borderColor: alpha("#1E8078", 0.3),
                      color: "#1E8078",
                      "&:hover": {
                        borderColor: "#1E8078",
                        backgroundColor: alpha("#1E8078", 0.05),
                      },
                    }}
                  >
                    Yesterday
                  </Button>
                </Box>
              </Box>

              <Divider />

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  height: "75vh",
                  overflow: "hidden",
                  paddingBottom: 2,
                }}
              >
                {" "}
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      backgroundColor: alpha(
                        theme.palette.background.default,
                        0.5
                      ),
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#1E8078",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Icon fontSize="small">store</Icon>
                      Sessions
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, overflow: "auto" }}>
                    {sessionsLoading ? (
                      <Stack spacing={1} sx={{ p: 2 }}>
                        {[1, 2, 3].map((i) => (
                          <Box key={i}>
                            <Skeleton
                              variant="rectangular"
                              height={48}
                              sx={{ borderRadius: 1 }}
                            />
                            <Box sx={{ pl: 2, mt: 1 }}>
                              <Skeleton
                                variant="rectangular"
                                height={40}
                                sx={{ borderRadius: 1, mb: 1 }}
                              />
                              <Skeleton
                                variant="rectangular"
                                height={40}
                                sx={{ borderRadius: 1 }}
                              />
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    ) : sessions.length === 0 ? (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          p: 3,
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 64,
                            color: alpha("#1E8078", 0.2),
                            mb: 2,
                          }}
                        >
                          calendar_today
                        </Icon>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "text.primary",
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          No Sessions Found
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.secondary",
                            maxWidth: 300,
                            mb: 3,
                          }}
                        >
                          There are no sessions recorded for{" "}
                          {selectedDate.format("MMMM D, YYYY")}
                        </Typography>
                        <Button
                          variant="outlined"
                          onClick={() => setSelectedDate(dayjs())}
                          startIcon={<Icon>today</Icon>}
                          sx={{
                            borderColor: alpha("#1E8078", 0.3),
                            color: "#1E8078",
                            "&:hover": {
                              borderColor: "#1E8078",
                              backgroundColor: alpha("#1E8078", 0.05),
                            },
                          }}
                        >
                          View Today's Sessions
                        </Button>
                      </Box>
                    ) : (
                      <List sx={{ p: 1 }}>
                        {Object.entries(groupedSessions).map(
                          ([branchName, branchSessions]) => (
                            <React.Fragment key={branchName}>
                              <ListItem
                                onClick={() => toggleBranch(branchName)}
                                sx={{
                                  backgroundColor: alpha("#1E8078", 0.04),
                                  borderRadius: 1,
                                  mb: 0.5,
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: alpha("#1E8078", 0.08),
                                    transform: "translateX(4px)",
                                  },
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      <Icon
                                        fontSize="small"
                                        sx={{ color: "#1E8078" }}
                                      >
                                        store
                                      </Icon>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {branchName}
                                      </Typography>
                                    </Box>
                                  }
                                  secondary={
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "text.secondary",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        mt: 0.5,
                                      }}
                                    >
                                      <Icon sx={{ fontSize: 14 }}>
                                        schedule
                                      </Icon>
                                      {branchSessions.length} active sessions
                                    </Typography>
                                  }
                                />
                                <Icon
                                  fontSize="small"
                                  sx={{
                                    transition: "transform 0.2s ease",
                                    transform: expandedBranches.includes(
                                      branchName
                                    )
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                    color: "#1E8078",
                                  }}
                                >
                                  expand_more
                                </Icon>
                              </ListItem>
                              <Collapse
                                in={expandedBranches.includes(branchName)}
                              >
                                <List
                                  component="div"
                                  disablePadding
                                  sx={{ pl: 2 }}
                                >
                                  {branchSessions.map((session) => (
                                    <ListItem
                                      key={session.id}
                                      onClick={() =>
                                        setSelectedSession(session.id)
                                      }
                                      sx={{
                                        borderRadius: 1,
                                        mb: 0.5,
                                        cursor: "pointer",
                                        transition: "all 0.2s ease",
                                        backgroundColor:
                                          selectedSession === session.id
                                            ? alpha("#1E8078", 0.1)
                                            : "transparent",
                                        "&:hover": {
                                          backgroundColor: alpha(
                                            "#1E8078",
                                            0.05
                                          ),
                                          transform: "translateX(4px)",
                                        },
                                      }}
                                    >
                                      <ListItemText
                                        primary={
                                          <Box sx={{ mb: 1 }}>
                                            <Typography
                                              component="span"
                                              variant="body2"
                                              sx={{
                                                fontWeight: 500,
                                                color:
                                                  selectedSession === session.id
                                                    ? "#1E8078"
                                                    : "text.primary",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                              }}
                                            >
                                              <Icon
                                                sx={{
                                                  fontSize: 18,
                                                  color:
                                                    selectedSession ===
                                                      session.id
                                                      ? "#1E8078"
                                                      : "text.secondary",
                                                }}
                                              >
                                                person
                                              </Icon>
                                              {session.cashier?.name ||
                                                "Unknown Cashier"}
                                            </Typography>
                                          </Box>
                                        }
                                        secondary={
                                          <Stack spacing={1} component="span">
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                              }}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 0.5,
                                                  backgroundColor: alpha(
                                                    "#1E8078",
                                                    0.05
                                                  ),
                                                  px: 1,
                                                  py: 0.5,
                                                  borderRadius: 1,
                                                }}
                                              >
                                                <Icon
                                                  sx={{
                                                    fontSize: 14,
                                                    color: "#1E8078",
                                                  }}
                                                >
                                                  schedule
                                                </Icon>
                                                <Typography
                                                  variant="caption"
                                                  sx={{ color: "#1E8078" }}
                                                >
                                                  {dayjs(
                                                    session.timestamps.start
                                                  ).format("h:mm A")}
                                                </Typography>
                                              </Box>
                                              {session.timestamps.end ? (
                                                <>
                                                  <Icon
                                                    sx={{
                                                      fontSize: 14,
                                                      color: "text.secondary",
                                                    }}
                                                  >
                                                    arrow_forward
                                                  </Icon>
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 0.5,
                                                      backgroundColor: alpha(
                                                        "#1E8078",
                                                        0.05
                                                      ),
                                                      px: 1,
                                                      py: 0.5,
                                                      borderRadius: 1,
                                                    }}
                                                  >
                                                    <Icon
                                                      sx={{
                                                        fontSize: 14,
                                                        color: "#1E8078",
                                                      }}
                                                    >
                                                      schedule
                                                    </Icon>
                                                    <Typography
                                                      variant="caption"
                                                      sx={{ color: "#1E8078" }}
                                                    >
                                                      {dayjs(
                                                        session.timestamps.end
                                                      ).format("h:mm A")}
                                                    </Typography>
                                                  </Box>
                                                </>
                                              ) : null}
                                              {!session.timestamps.end && (
                                                <Chip
                                                  label="Active"
                                                  size="small"
                                                  sx={{
                                                    height: 24,
                                                    backgroundColor: alpha(
                                                      theme.palette.success
                                                        .main,
                                                      0.1
                                                    ),
                                                    color:
                                                      theme.palette.success
                                                        .main,
                                                    fontWeight: 500,
                                                  }}
                                                />
                                              )}
                                            </Box>
                                          </Stack>
                                        }
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Collapse>
                            </React.Fragment>
                          )
                        )}
                      </List>
                    )}
                  </Box>
                </Paper>
                <TabContext value={String(activeTab)}>
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 2,
                      borderRadius: 1,
                      overflow: "hidden",
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {sessions.length === 0 ? (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          p: 3,
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: 64,
                            color: alpha("#1E8078", 0.2),
                            mb: 2,
                          }}
                        >
                          receipt_long
                        </Icon>
                        <Typography
                          variant="h6"
                          sx={{
                            color: "text.primary",
                            fontWeight: 600,
                            mb: 1,
                          }}
                        >
                          No Data Available
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.secondary",
                            maxWidth: 300,
                          }}
                        >
                          Select a date with active sessions to view orders,
                          transactions, and summary details
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            backgroundColor: alpha(
                              theme.palette.background.default,
                              0.5
                            ),
                          }}
                        >
                          <Tabs
                            value={activeTab}
                            onChange={(_, newValue: number) =>
                              setActiveTab(newValue)
                            }
                            sx={{
                              px: 2,
                              "& .MuiTabs-indicator": {
                                backgroundColor: "#1E8078",
                              },
                              "& .MuiTab-root": {
                                color: "text.secondary",
                                "&.Mui-selected": {
                                  color: "#1E8078",
                                },
                              },
                            }}
                          >
                            <Tab
                              label={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Icon fontSize="small">receipt</Icon>
                                  Orders
                                </Box>
                              }
                            />
                            <Tab
                              label={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Icon fontSize="small">payments</Icon>
                                  Transactions
                                </Box>
                              }
                            />
                            <Tab
                              label={
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Icon fontSize="small">summarize</Icon>
                                  Summary
                                </Box>
                              }
                            />
                          </Tabs>
                          <Box sx={{ display: "flex", gap: 1, p: 1, pr: 2 }}>
                            {activeTab !== 2 && (
                              <Tooltip title="Refresh data">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (activeTab === 0) {
                                      fetchSessionOrders();
                                      toast.success("Orders refreshed");
                                    } else {
                                      fetchSessionTransactions();
                                      toast.success("Transactions refreshed");
                                    }
                                  }}
                                  sx={{
                                    color: "text.secondary",
                                    "&:hover": {
                                      color: "#1E8078",
                                      backgroundColor: alpha("#1E8078", 0.05),
                                    },
                                  }}
                                >
                                  <Icon fontSize="small">refresh</Icon>
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Export to Excel">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  toast.info(
                                    "Export functionality coming soon"
                                  );
                                }}
                                sx={{
                                  color: "text.secondary",
                                  "&:hover": {
                                    color: "#1E8078",
                                    backgroundColor: alpha("#1E8078", 0.05),
                                  },
                                }}
                              >
                                <Icon fontSize="small">download</Icon>
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            height: "100%",
                          }}
                        >
                          <TabPanel
                            value="0"
                            sx={{
                              padding: "0px !important",
                              overflowY: "auto",
                              height: "100%",
                              flex: 1,
                            }}
                          >
                            <DayBookOrdersPanel
                              loading={ordersLoading}
                              data={orders}
                            />
                          </TabPanel>
                          <TabPanel
                            value="1"
                            sx={{
                              padding: "0px !important",
                              overflowY: "auto",
                              height: "100%",
                              flex: 1,
                            }}
                          >
                            <DayBookTransactionsPanel
                              loading={transactionsLoading}
                              data={transactions}
                            />
                          </TabPanel>
                          <TabPanel
                            value="2"
                            sx={{
                              padding: "0px !important",
                              overflowY: "auto",
                              height: "100%",
                              flex: 1,
                            }}
                          >
                            <DayBookSummaryPanel
                              loading={sessionsLoading}
                              sessions={sessions as any}
                              selectedSession={selectedSession}
                            />
                          </TabPanel>
                        </Box>
                      </>
                    )}
                  </Paper>
                </TabContext>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};
